import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import { useNavigate, Link } from 'react-router-dom';
    import { useAuth } from '../context/AuthContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiUserPlus, FiMail, FiLock, FiEye, FiEyeOff } = FiIcons;

    const SignUp = () => {
        const [email, setEmail] = useState('');
        const [password, setPassword] = useState('');
        const [showPassword, setShowPassword] = useState(false);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        const [success, setSuccess] = useState(false);
        const { signUp, user } = useAuth();
        const navigate = useNavigate();

        useEffect(() => {
            if (user) {
                navigate('/dashboard');
            }
        }, [user, navigate]);

        const handleSubmit = async (e) => {
            e.preventDefault();
            setLoading(true);
            setError('');
            setSuccess(false);
            const { error } = await signUp(email, password);
            if (error) {
                setError(error.message);
            } else {
                setSuccess(true);
            }
            setLoading(false);
        };
        
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 w-full max-w-md"
                >
                    <div className="text-center mb-8">
                        <div className="mx-auto w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                            <SafeIcon icon={FiUserPlus} className="text-2xl text-slate-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900 mb-1">Create Account</h1>
                        <p className="text-slate-600">Join our community of creators</p>
                    </div>

                    {success ? (
                        <div className="text-center">
                            <h2 className="text-xl font-semibold text-green-700 mb-4">Account Created!</h2>
                            <p className="text-slate-600 mb-6">Please check your email to confirm your account and then log in.</p>
                            <Link
                                to="/login"
                                className="w-full inline-block bg-slate-900 text-white py-2.5 rounded-lg hover:bg-slate-800 transition-colors font-semibold"
                            >
                                Go to Login
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}
                             <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                <div className="relative">
                                    <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                                <div className="relative">
                                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full pl-10 pr-12 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                        <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                                    </button>
                                </div>
                            </div>

                            <button type="submit" disabled={loading} className="w-full bg-slate-900 text-white py-2.5 rounded-lg hover:bg-slate-800 transition-colors font-semibold disabled:opacity-50">
                                {loading ? 'Creating account...' : 'Sign Up'}
                            </button>
                        </form>
                    )}

                    {!success && (
                        <div className="mt-6 text-center text-sm">
                            <p className="text-slate-600">
                                Already have an account?{' '}
                                <Link to="/login" className="font-medium text-slate-900 hover:underline">
                                    Log in
                                </Link>
                            </p>
                             <p className="mt-4">
                               <Link to="/" className="text-slate-500 hover:text-slate-700 transition-colors">
                                 ← Back to Directory
                               </Link>
                            </p>
                        </div>
                    )}
                </motion.div>
            </div>
        );
    };

    export default SignUp;